import { useState } from 'react';
import { queryClient, queryKeys } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import type { WriteDirectory } from './file-browser';

// The client half of editing a file and creating one: POST the path and the body, then make the workspace
// re-read so the gallery, the tiles and their preview text all show what was just saved.
//
// The rule about WHICH writes are legal is not here — it is `planFileWrite` in common-lib, which the route
// and the native bridge both run, so a client cannot talk any surface into an illegal write by asking
// nicely. This module only asks, reports, and refreshes.

export type FileWriteState = 'idle' | 'saving' | 'error';

/** Why a save failed, when the server named a reason worth showing the user rather than a generic failure. */
export type FileWriteError = 'file_exists' | 'not_editable' | 'content_too_large' | 'file_not_found' | 'failed';

export function useFileWrite() {
  const [state, setState] = useState<FileWriteState>('idle');
  const [error, setError] = useState<FileWriteError | null>(null);

  /**
   * Writes `content` to `path`. Pass `create` when the file is expected NOT to exist — the server refuses
   * with `file_exists` rather than overwriting, which is the only protection against a new file silently
   * replacing an agent's file of the same name (the file-system keeps no revisions).
   *
   * Resolves to true on success.
   */
  const write = async (
    path: string,
    content: string,
    options?: {
      create?: boolean;
      directory?: WriteDirectory;
      /** The file's description — the file-system's `hint`, which the agent reads to decide when the file
       *  is worth opening. Omitted or empty KEEPS whatever the file already had; see the route. */
      hint?: string;
    },
  ): Promise<boolean> => {
    setState('saving');
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/write-file`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          path,
          content,
          create: options?.create === true,
          directory: options?.directory,
          ...(options?.hint ? { hint: options.hint } : {}),
        }),
      });
      if (!res.ok) {
        const reason = await res
          .json()
          .then((body) => body?.error)
          .catch(() => undefined);
        setError(KNOWN_ERRORS.includes(reason) ? reason : 'failed');
        setState('error');
        return false;
      }
      // The per-workspace list AND the account-wide one, which is its own cache entry — the cross-workspace
      // `/files` browser and the Files summary tiles read it, and a per-workspace key never reaches it.
      const workspaceId = path.split('/')[0];
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFiles(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.allWorkspaceFiles });
      setState('idle');
      return true;
    } catch {
      setError('failed');
      setState('error');
      return false;
    }
  };

  return { state, error, write, reset: () => { setState('idle'); setError(null); } };
}

const KNOWN_ERRORS: string[] = ['file_exists', 'not_editable', 'content_too_large', 'file_not_found'];
